import { submissionsInFillingProcessApi } from '@api';
import { ISubmission, SubmissionCreationMode, SubmissionStatus } from '@app-types';
import termsOfUsePdfFile from '@assets/termsOfUse.pdf';
import policyPdfFile from '@assets/privacyPolicy.pdf';
import { StepperContext } from '@components/Stepper';
import { LocalStorageKey } from '@constants';
import { fetchAndHashPdf, isLocaleMessageExists } from '@utils';
import eventBus from '@eventBus';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useCalculateAllowance,
  useCreateSubmission,
  useCreateSubmissionInFillingProcess,
  useUpdateSubmissionInFillingProcess,
} from '@hooks';
import { getCurrentSubmission, selectCurrentSubmission } from '@store/submission/slice';
import { useQuery } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
  FieldArrayWithId,
  FormProvider,
  Resolver,
  ResolverOptions,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  WatchObserver,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useBlocker } from 'react-router-dom';
import * as yup from 'yup';
import dayjs, { Dayjs } from 'dayjs';
import { selectCustomer } from '@store/customer/selectors';
import { useAppDispatch } from '@store';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import {
  convertPrefilledSubmissionToFormValues,
  convertSubmissionFormValuesToDto,
  convertSubmissionFormValuesToExpectedAllowance,
  convertSubmissionFormValuesToUpdateDto,
  convertSubmissionToFormValues,
} from '../dto';
import { ChildFormValues, SubmissionCreationFormValues, SubmissionRadioValue } from '../types';
import ChildBenefit from './ChildBenefit';
import CounselingRecognizedBody from './CounselingRecognizedBody';
import InfoBox from './InfoBox';
import MaintenanceObligations from './MaintenanceObligations';
import OngoingServices from './OngoingServices';
import { Form, StyledSubmissionFormFooter } from './styles';

interface ISubmissionFormContext {
  getChildrenValue: (index: number, key: keyof ChildFormValues) => any;
  childrenFields: FieldArrayWithId[];
  appendChild: UseFieldArrayAppend<SubmissionCreationFormValues, 'children'>;
  removeChild: UseFieldArrayRemove;
}

export const SubmissionFormContext = createContext<ISubmissionFormContext>({
  getChildrenValue: () => {},
  childrenFields: [],
  appendChild: () => {},
  removeChild: () => {},
});

const useSubmissionFormSchemaAndMethods = (
  childrenArray: ChildFormValues[],
  lowerLimitForChildBirthDate: Dayjs | null,
) => {
  const { formatMessage } = useIntl();
  let combinedSchema = yup.object();

  combinedSchema = combinedSchema.shape({
    hasSpouse: yup.string().test('is-required', function (value) {
      const { createError } = this;
      if (!value) {
        return createError({
          message: formatMessage({ id: 'submissionCreation.validation.radioButton.title' }),
          type: 'select-is-empty',
        });
      }
      return true;
    }),
    isThereSpouseHousehold: yup.mixed().when('hasSpouse', {
      // @ts-ignore
      is: 'yes',
      then: () => yup.string().test('is-required', function (value) {
        const { createError } = this;
        if (!value) {
          return createError({
            message: formatMessage({ id: 'submissionCreation.validation.radioButton.title' }),
            type: 'select-is-empty',
          });
        }
        return true;
      }),
    }),
    isThereSpouseMaintenance: yup.mixed().when('isThereSpouseHousehold', {
      // @ts-ignore
      is: 'no',
      then: () => yup.string().test('is-required', function (value) {
        const { createError } = this;
        if (!value) {
          return createError({
            message: formatMessage({ id: 'submissionCreation.validation.radioButton.title' }),
            type: 'select-is-empty',
          });
        }
        return true;
      }),
    }),
    spouseFirstName: yup.string().when(['hasSpouse', 'isThereSpouseHousehold', 'isThereSpouseMaintenance'], {
      // @ts-ignore
      is: (hasSpouse, isThereSpouseHousehold, isThereSpouseMaintenance) =>
        hasSpouse === 'yes' && (isThereSpouseHousehold === 'yes' || isThereSpouseMaintenance === 'yes'),
      then: () => yup.string().required(formatMessage({ id: 'submissionCreation.validation.firstName.title' })),
    }),
    spouseLastName: yup.string().when(['hasSpouse', 'isThereSpouseHousehold', 'isThereSpouseMaintenance'], {
      // @ts-ignore
      is: (hasSpouse, isThereSpouseHousehold, isThereSpouseMaintenance) =>
        hasSpouse === 'yes' && (isThereSpouseHousehold === 'yes' || isThereSpouseMaintenance === 'yes'),
      then: () => yup.string().required(formatMessage({ id: 'submissionCreation.validation.lastName.title' })),
    }),
    spouseBirthDate: yup.date().when(['hasSpouse', 'isThereSpouseHousehold', 'isThereSpouseMaintenance'], {
      // @ts-ignore
      is: (hasSpouse, isThereSpouseHousehold, isThereSpouseMaintenance) =>
        hasSpouse === 'yes' && (isThereSpouseHousehold === 'yes' || isThereSpouseMaintenance === 'yes'),
      then: () =>
        yup
          .date()
          .required(formatMessage({ id: 'submissionCreation.validation.birthDate.required.title' }))
          .test('invalid-date-of-birth', formatMessage({ id: 'submissionCreation.validation.birthDate.general.invalid.title' }), (value) => {
            if (dayjs(value).isAfter(dayjs()))
              return false;

            return true;
          })
          .test('maximum-age', formatMessage({ id: 'submissionCreation.validation.birthDate.maximumAge.title' }), (value) => {
            if (!value)
              return true;

            const oneHundredTwentyYearsAgo = dayjs().subtract(120, 'year');
            return dayjs(value).isAfter(oneHundredTwentyYearsAgo) || dayjs(value).isSame(oneHundredTwentyYearsAgo, 'day');
          }),
    }),
    marriageCertificateFiles: yup.mixed().when(['hasSpouse', 'isThereSpouseHousehold', 'isThereSpouseMaintenance'], {
      // @ts-ignore
      is: (hasSpouse, isThereSpouseHousehold, isThereSpouseMaintenance) =>
        hasSpouse === 'yes' && (isThereSpouseHousehold === 'yes' || isThereSpouseMaintenance === 'yes'),
      then: () =>
        yup
          .mixed()
          .test(
            'is-defined',
            formatMessage({ id: 'submissionCreation.validation.documents.title' }),
            function (value, context) {
              const { uploadedMarriageCertificateFiles, isThereSpouseHousehold, isThereSpouseMaintenance } = context.parent;
              const { createError } = this;

              const hasNoFiles = (v: any) => v === undefined || (Array.isArray(v) && v.length === 0);
              const hasLessThanTwoFiles = (v: any) => Array.isArray(v) && v.length < 2;

              if (
                (hasNoFiles(value) && hasNoFiles(uploadedMarriageCertificateFiles)) ||
                (isThereSpouseHousehold === 'no' && isThereSpouseMaintenance === 'yes' &&
                  (hasLessThanTwoFiles(value) || hasLessThanTwoFiles(uploadedMarriageCertificateFiles) ||
                    (Array.isArray(value) && Array.isArray(uploadedMarriageCertificateFiles) &&
                      value.length + uploadedMarriageCertificateFiles.length < 2)))
              ) {
                return createError({
                  message: formatMessage({ id: 'submissionCreation.validation.documents.title' }),
                });
              }
              return true;
            },
          ),
      otherwise: () => yup.mixed().nullable(),
    }),
  });

  if (childrenArray.length > 0) {
    combinedSchema = combinedSchema.shape({
      children: yup.array().of(
        yup.object().shape({
          isThereHousehold: yup.boolean(),
          isThereMaintenance: yup.boolean(),
          isThereCreditedToPAccount: yup.boolean(),
          firstName: yup.string().required(formatMessage({ id: 'submissionCreation.validation.firstName.title' })),
          lastName: yup.string().required(formatMessage({ id: 'submissionCreation.validation.lastName.title' })),
          birthDate:
            yup
              .date()
              .required(formatMessage({ id: 'submissionCreation.validation.birthDate.required.title' }))
              .test('upper-limit-date-of-birth', formatMessage({ id: 'submissionCreation.validation.birthDate.general.invalid.title' }), (value) => {
                if (dayjs(value).isAfter(dayjs()))
                  return false;

                return true;
              })
              .test('lower-limit-date-of-birth', formatMessage({ id: 'submissionCreation.validation.birthDate.child.invalid.title' }), (value) => {
                if (dayjs(value).isBefore(lowerLimitForChildBirthDate))
                  return false;

                return true;
              }),
          isBenefits: yup.boolean(),
          supplement: yup.string(),
          childDocuments: yup
            .mixed()
            .test(
              'is-defined',
              formatMessage({ id: 'submissionCreation.validation.documents.title' }),
              function (value, context) {
                const children = context.from?.[1]?.value?.children || [];
                const hasDocumentsAtLeastOneChild = children.some(
                  (child: ChildFormValues) => !!child.childDocuments || !!child.uploadedChildDocuments,
                );

                const { uploadedChildDocuments } = context.parent;
                const { createError } = this;
                if (
                  !hasDocumentsAtLeastOneChild &&
                  (value === undefined || (Array.isArray(value) && value.length === 0)) &&
                  (uploadedChildDocuments === undefined ||
                    (Array.isArray(uploadedChildDocuments) && uploadedChildDocuments.length === 0))
                ) {
                  return createError({
                    message: formatMessage({ id: 'submissionCreation.validation.documents.title' }),
                  });
                }
                return true;
              },
            ),
          supplementDocuments: yup
            .mixed()
            .test(
              'is-defined',
              formatMessage({ id: 'submissionCreation.validation.documents.title' }),
              function (value, context) {
                const children = context.from?.[1]?.value?.children || [];
                const hasDocumentsAtLeastOneChild = children.some(
                  (child: ChildFormValues) => !!child.supplementDocuments || !!child.uploadedSupplementDocuments,
                );

                const { uploadedSupplementDocuments, isBenefits, supplement } = context.parent;
                const { createError } = this;
                if (
                  !hasDocumentsAtLeastOneChild &&
                  (isBenefits || supplement) &&
                  (value === undefined || (Array.isArray(value) && value.length === 0)) &&
                  (uploadedSupplementDocuments === undefined ||
                    (Array.isArray(uploadedSupplementDocuments) && uploadedSupplementDocuments.length === 0))
                ) {
                  return createError({
                    message: formatMessage({ id: 'submissionCreation.validation.documents.title' }),
                  });
                }
                return true;
              },
            ),
        }),
      ),
    });
  }

  combinedSchema = combinedSchema.shape({
    isUserReceivedOngoingBenefit: yup
      .string()
      .test('is-required', function (value) {
        const { createError } = this;
        if (!value) {
          return createError({
            message: formatMessage({ id: 'submissionCreation.validation.radioButton.title' }),
            type: 'select-is-empty',
          });
        }
        return true;
      }),
    ongoingBenefit: yup.object().when('isUserReceivedOngoingBenefit', {
      // @ts-ignore
      is: 'yes',
      then: () =>
        yup.object().shape({
          amount: yup.string(),
          furtherPersonsNumber: yup.string(),
          documents: yup
            .mixed()
            .test(
              'is-defined',
              formatMessage({ id: 'submissionCreation.validation.documents.title' }),
              function (value, context) {
                const { uploadedDocuments, amount, furtherPersonsNumber } = context.parent;
                const { createError } = this;
                if (
                  (amount || furtherPersonsNumber) &&
                  (value === undefined || (Array.isArray(value) && value.length === 0)) &&
                  (uploadedDocuments === undefined || (Array.isArray(uploadedDocuments) && uploadedDocuments.length === 0))
                ) {
                  return createError({
                    message: formatMessage({ id: 'submissionCreation.validation.documents.title' }),
                  });
                }
                return true;
              },
            ),
        }),
      otherwise: () =>
        yup.object().shape({
          amount: yup.string(),
          furtherPersonsNumber: yup.string(),
          documents: yup.mixed(),
        }),
    }),

    isUserReceivedOneOffBenefits: yup
      .string()
      .test('is-required', function (value) {
        const { createError } = this;
        if (!value) {
          return createError({
            message: formatMessage({ id: 'submissionCreation.validation.radioButton.title' }),
            type: 'select-is-empty',
          });
        }
        return true;
      }),
    oneOffBenefits: yup.array().when('isUserReceivedOneOffBenefits', {
      // @ts-ignore
      is: 'yes',
      then: () =>
        yup.array()
          .test('min', function (value) {
            const { createError } = this;
            if (value === undefined || (Array.isArray(value) && value.length === 0)) {
              return createError({
                message: formatMessage({ id: 'submissionCreation.validation.select.title' }),
                type: 'select-is-empty',
              });
            }
            return true;
          })
          .of(
            yup.object().shape({
              amount: yup.string().required(formatMessage({ id: 'submissionCreation.validation.amount.required.title' })),
              documents: yup
                .mixed()
                .test(
                  'is-defined',
                  formatMessage({ id: 'submissionCreation.validation.documents.title' }),
                  function (value, context) {
                    const { uploadedDocuments } = context.parent;
                    const { createError } = this;
                    if (
                      (value === undefined || (Array.isArray(value) && value.length === 0)) &&
                      (uploadedDocuments === undefined ||
                        (Array.isArray(uploadedDocuments) && uploadedDocuments.length === 0))
                    ) {
                      return createError({
                        message: formatMessage({ id: 'submissionCreation.validation.documents.title' }),
                      });
                    }
                    return true;
                  },
                ),
            }),
          ),

      otherwise: () =>
        yup.array().of(
          yup.object().shape({
            amount: yup.string(),
            documents: yup.mixed(),
          }),
        ),
    }),

    isUserReceivedBackPaymentBenefits: yup
      .string()
      .test('is-required', function (value) {
        const { createError } = this;
        if (!value) {
          return createError({
            message: formatMessage({ id: 'submissionCreation.validation.radioButton.title' }),
            type: 'select-is-empty',
          });
        }
        return true;
      }),
    backPaymentBenefits: yup.array().when('isUserReceivedBackPaymentBenefits', {
      // @ts-ignore
      is: 'yes',
      then: () =>
        yup.array()
          .test('min', function (value) {
            const { createError } = this;
            if (value === undefined || (Array.isArray(value) && value.length === 0)) {
              return createError({
                message: formatMessage({ id: 'submissionCreation.validation.select.title' }),
                type: 'select-is-empty',
              });
            }
            return true;
          })
          .of(
            yup.object().shape({
              amount: yup.string().required(formatMessage({ id: 'submissionCreation.validation.amount.required.title' })),
              documents: yup
                .mixed()
                .test(
                  'is-defined',
                  formatMessage({ id: 'submissionCreation.validation.documents.title' }),
                  function (value, context) {
                    const { uploadedDocuments } = context.parent;
                    const { createError } = this;
                    if (
                      (value === undefined || (Array.isArray(value) && value.length === 0)) &&
                      (uploadedDocuments === undefined ||
                        (Array.isArray(uploadedDocuments) && uploadedDocuments.length === 0))
                    ) {
                      return createError({
                        message: formatMessage({ id: 'submissionCreation.validation.documents.title' }),
                      });
                    }
                    return true;
                  },
                ),
            }),
          ),

      otherwise: () =>
        yup.array().of(
          yup.object().shape({
            amount: yup.string(),
            documents: yup.mixed(),
          }),
        ),
    }),

    isUserReceivedHealthDamages: yup
      .string()
      .test('is-required', function (value) {
        const { createError } = this;
        if (!value) {
          return createError({
            message: formatMessage({ id: 'submissionCreation.validation.radioButton.title' }),
            type: 'select-is-empty',
          });
        }
        return true;
      }),
    healthDamages: yup.array().when('isUserReceivedHealthDamages', {
      // @ts-ignore
      is: 'yes',
      then: () =>
        yup.array()
          .test('min', function (value) {
            const { createError } = this;
            if (value === undefined || (Array.isArray(value) && value.length === 0)) {
              return createError({
                message: formatMessage({ id: 'submissionCreation.validation.select.title' }),
                type: 'select-is-empty',
              });
            }
            return true;
          })
          .of(
            yup.object().shape({
              amount: yup.string().required(formatMessage({ id: 'submissionCreation.validation.amount.required.title' })),
              documents: yup
                .mixed()
                .test(
                  'is-defined',
                  formatMessage({ id: 'submissionCreation.validation.documents.title' }),
                  function (value, context) {
                    const { uploadedDocuments } = context.parent;
                    const { createError } = this;
                    if (
                      (value === undefined || (Array.isArray(value) && value.length === 0)) &&
                      (uploadedDocuments === undefined ||
                        (Array.isArray(uploadedDocuments) && uploadedDocuments.length === 0))
                    ) {
                      return createError({
                        message: formatMessage({ id: 'submissionCreation.validation.documents.title' }),
                      });
                    }
                    return true;
                  },
                ),
            }),
          ),
      otherwise: () =>
        yup.array().of(
          yup.object().shape({
            amount: yup.string(),
            documents: yup.mixed(),
          }),
        ),
    }),

    isUserReceivedMotherChildFoundations: yup
      .string()
      .test('is-required', function (value) {
        const { createError } = this;
        if (!value) {
          return createError({
            message: formatMessage({ id: 'submissionCreation.validation.radioButton.title' }),
            type: 'select-is-empty',
          });
        }
        return true;
      }),
    motherChildFoundations: yup.array().when('isUserReceivedMotherChildFoundations', {
      // @ts-ignore
      is: 'yes',
      then: () =>
        yup.array().of(
          yup.object().shape({
            amount: yup.string().required(formatMessage({ id: 'submissionCreation.validation.amount.required.title' })),
            documents: yup
              .mixed()
              .test(
                'is-defined',
                formatMessage({ id: 'submissionCreation.validation.documents.title' }),
                function (value, context) {
                  const { uploadedDocuments } = context.parent;
                  const { createError } = this;
                  if (
                    (value === undefined || (Array.isArray(value) && value.length === 0)) &&
                    (uploadedDocuments === undefined ||
                      (Array.isArray(uploadedDocuments) && uploadedDocuments.length === 0))
                  ) {
                    return createError({
                      message: formatMessage({ id: 'submissionCreation.validation.documents.title' }),
                    });
                  }
                  return true;
                },
              ),
          }),
        ),

      otherwise: () =>
        yup.array().of(
          yup.object().shape({
            amount: yup.string(),
            documents: yup.mixed(),
          }),
        ),
    }),

    isUserReceivedBackPaymentWagePensionBenefits: yup
      .string()
      .test('is-required', function (value) {
        const { createError } = this;
        if (!value) {
          return createError({
            message: formatMessage({ id: 'submissionCreation.validation.radioButton.title' }),
            type: 'select-is-empty',
          });
        }
        return true;
      }),
    backPaymentWagePensionBenefits: yup.array().when('isUserReceivedBackPaymentWagePensionBenefits', {
      // @ts-ignore
      is: 'yes',
      then: () =>
        yup.array().of(
          yup.object().shape({
            amount: yup
              .string()
              .required(formatMessage({ id: 'submissionCreation.validation.amount.required.title' }))
              .test('limit-amount', formatMessage({ id: 'submissionCreation.validation.amount.limit.title' }), (value) => {
                if (parseFloat(value) > 500)
                  return false;

                return true;
              }),
            documents: yup
              .mixed()
              .test(
                'is-defined',
                formatMessage({ id: 'submissionCreation.validation.documents.title' }),
                function (value, context) {
                  const { uploadedDocuments } = context.parent;
                  const { createError } = this;
                  if (
                    (value === undefined || (Array.isArray(value) && value.length === 0)) &&
                    (uploadedDocuments === undefined ||
                      (Array.isArray(uploadedDocuments) && uploadedDocuments.length === 0))
                  ) {
                    return createError({
                      message: formatMessage({ id: 'submissionCreation.validation.documents.title' }),
                    });
                  }
                  return true;
                },
              ),
          }),
        ),
      otherwise: () =>
        yup.array().of(
          yup.object().shape({
            amount: yup.string(),
            documents: yup.mixed(),
          }),
        ),
    }),

    isUserReceivedAdditionalBenefits: yup
      .string()
      .test('is-required', function (value) {
        const { createError } = this;
        if (!value) {
          return createError({
            message: formatMessage({ id: 'submissionCreation.validation.radioButton.title' }),
            type: 'select-is-empty',
          });
        }
        return true;
      }),
    additionalBenefits: yup.array().when('isUserReceivedAdditionalBenefits', {
      // @ts-ignore
      is: 'yes',
      then: () =>
        yup.array()
          .test('min', function (value) {
            const { createError } = this;
            if (value === undefined || (Array.isArray(value) && value.length === 0)) {
              return createError({
                message: formatMessage({ id: 'submissionCreation.validation.select.title' }),
                type: 'select-is-empty',
              });
            }
            return true;
          })
          .of(
            yup.object().shape({
              amount: yup.string().required(formatMessage({ id: 'submissionCreation.validation.amount.required.title' })),
              documents: yup
                .mixed()
                .test(
                  'is-defined',
                  formatMessage({ id: 'submissionCreation.validation.documents.title' }),
                  function (value, context) {
                    const { uploadedDocuments } = context.parent;
                    const { createError } = this;
                    if (
                      (value === undefined || (Array.isArray(value) && value.length === 0)) &&
                      (uploadedDocuments === undefined ||
                        (Array.isArray(uploadedDocuments) && uploadedDocuments.length === 0))
                    ) {
                      return createError({
                        message: formatMessage({ id: 'submissionCreation.validation.documents.title' }),
                      });
                    }
                    return true;
                  },
                ),
            }),
          ),
      otherwise: () =>
        yup.array().of(
          yup.object().shape({
            amount: yup.string(),
            documents: yup.mixed(),
          }),
        ),
    }),
  });

  const schema = combinedSchema.shape({
    isUserAlreadyCounseling: yup.string().test('is-required', function (value) {
      const { createError } = this;
      if (!value) {
        return createError({
          message: formatMessage({ id: 'submissionCreation.validation.radioButton.title' }),
          type: 'select-is-empty',
        });
      }
      return true;
    }),
    recognizedBodyId: yup.string().when('isUserAlreadyCounseling', {
      is: 'yes',
      then: () => yup.string().test('is-required', function (value) {
        const { createError } = this;
        if (!value) {
          return createError({
            message: formatMessage({ id: 'submissionCreation.validation.radioButton.title' }),
            type: 'select-is-empty',
          });
        }
        return true;
      }),
    }),
    confirmAccurancy: yup
      .boolean()
      .oneOf([true], formatMessage({ id: 'submissionCreation.validation.checkbox.title' }))
      .required(formatMessage({ id: 'submissionCreation.validation.checkbox.title' })),
    conseptTheProcessing: yup
      .boolean()
      .oneOf([true], formatMessage({ id: 'submissionCreation.validation.checkbox.title' }))
      .required(formatMessage({ id: 'submissionCreation.validation.checkbox.title' })),
    isUserReadTermsOfUse: yup
      .boolean()
      .oneOf([true], formatMessage({ id: 'submissionCreation.validation.readTermsOfUse' }))
      .required(formatMessage({ id: 'submissionCreation.validation.readTermsOfUse' })),
    isUserReadDeclarationOfConsent: yup
      .boolean()
      .oneOf([true], formatMessage({ id: 'submissionCreation.validation.readDeclarationOfConsent' }))
      .required(formatMessage({ id: 'submissionCreation.validation.readDeclarationOfConsent' })),
    noOneReason: yup
      .boolean()
      .oneOf([true], formatMessage({ id: 'submissionCreation.validation.noOneReason' })),
  });

  const methods = useForm<SubmissionCreationFormValues>({
    resolver: yupResolver(schema) as unknown as Resolver<
      SubmissionCreationFormValues,
      ResolverOptions<SubmissionCreationFormValues>
    >,
  });

  return useMemo(() => ({ combinedSchema, schema, methods }), [combinedSchema, schema, methods]);
};

const SubmissionForm = () => {
  const account = useSelector(selectCustomer);
  const dispatch = useAppDispatch();

  const { formatMessage, messages } = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const { mutateAsync: createSubmission } = useCreateSubmission();

  const submissionInFillingProcess = useQuery({
    queryKey: ['submissionInFillingProcess'],
    queryFn: () => submissionsInFillingProcessApi.getCurrentSubmissionInFillingProcess().then(({ data }) => data),
  });
  const { setCurrentStep, goToNextStep } = useContext(StepperContext);

  const [childrenArray, setChildrenArray] = useState<ChildFormValues[]>([]);
  const [isChangesUnsaved, setIsChangesUnsaved] = useState(false);
  const [navigateConfirmModal, setNavigateConfirmModal] = useState(false);
  const [isScrollActivated, setScrollActivated] = useState(false);

  const { methods } = useSubmissionFormSchemaAndMethods(childrenArray, dayjs(account?.dateOfBirth));
  const {
    control,
    watch,
    handleSubmit,
    reset,
    getValues,
    formState: { isSubmitting, isDirty },
  } = methods;

  const {
    fields: childrenFields,
    append: appendChild,
    remove: removeChild,
  } = useFieldArray({
    control,
    name: 'children',
  });

  const [prevFormValues, setPrevFormValues] = useState<SubmissionCreationFormValues | null>(null);

  const { mutateAsync: updateSubmissionInFillingProcess } = useUpdateSubmissionInFillingProcess({
    onSuccess: (res) => {
      if (res instanceof AxiosError) {
        const errorData = res?.response?.data;
        const message = `submissionCreation.filling.serverError[${errorData?.message}]`;

        const localeMessage =
          errorData?.message === 'Virus detected'
            ? formatMessage({ id: message }, { infectedFiles: errorData?.infectedFiles?.join(', ') })
            : formatMessage({ id: isLocaleMessageExists(message, messages) ? message : 'error.common' });

        enqueueSnackbar(localeMessage, {
          variant: 'error',
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
        });
      } else {
        setPrevFormValues(getValues());
      }
    },
  });
  const { mutateAsync: createSubmissionInFillingProcess } = useCreateSubmissionInFillingProcess({
    onSuccess: (res) => {
      if (res instanceof AxiosError) {
        const errorData = res?.response?.data;
        const message = `submissionCreation.filling.serverError[${errorData?.message}]`;

        const localeMessage =
          errorData?.message === 'Virus detected'
            ? formatMessage({ id: message }, { infectedFiles: errorData?.infectedFiles?.join(', ') })
            : formatMessage({ id: isLocaleMessageExists(message, messages) ? message : 'error.common' });

        enqueueSnackbar(localeMessage, {
          variant: 'error',
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
        });
      } else {
        submissionInFillingProcess.refetch();
        setPrevFormValues(getValues());
      }
    },
  });

  const currentSubmission = useSelector(selectCurrentSubmission);

  useEffect(() => {
    setChildrenArray(childrenFields);
  }, [childrenFields]);

  useEffect(() => {
    if (!submissionInFillingProcess.data)
      return;

    reset(convertSubmissionToFormValues(submissionInFillingProcess.data as unknown as ISubmission), { keepDirty: false });
  }, [submissionInFillingProcess?.data?.id]);

  useEffect(() => {
    const isWithPrefilled = localStorage.getItem(LocalStorageKey.SubmissionCreationMode) === SubmissionCreationMode.WithPrefilled;

    if (!isWithPrefilled || !currentSubmission || submissionInFillingProcess.data)
      return;

    reset(convertPrefilledSubmissionToFormValues(currentSubmission as unknown as ISubmission), { keepDirty: false });
  }, [currentSubmission?.id]);

  // Synchronous update of currentStep to avoid step glare on reload page (if currentStep is a two/three)
  useLayoutEffect(() => {
    if (!currentSubmission)
      return;

    if (localStorage.getItem(LocalStorageKey.SubmissionCreationMode))
      return setCurrentStep(0);

    switch (currentSubmission.status) {
      case SubmissionStatus.New:
      case SubmissionStatus.InProcessing:
      case SubmissionStatus.DocumentRequested:
      case SubmissionStatus.Update:
        setCurrentStep(1);
        break;
      case SubmissionStatus.Completed:
      case SubmissionStatus.Withdrawn:
      case SubmissionStatus.Archived:
        setCurrentStep(2);
        break;
      default:
        setCurrentStep(0);
        break;
    }
  }, [currentSubmission?.status]);

  const getChildrenValue = (index: number, key: keyof ChildFormValues) => {
    return watch(`children[${index}].${key}` as unknown as WatchObserver<SubmissionCreationFormValues>);
  };

  const ongoingBenefitAmount = watch('ongoingBenefit.amount');
  const ongoingBenefitFurtherPersonsNumber = watch('ongoingBenefit.furtherPersonsNumber');
  const isUserReceivedOngoingBenefit = watch('isUserReceivedOngoingBenefit');

  const [isOngoingBenefitsValuesUndefinedOrEmpty, setIsOngoingBenefitsValuesUndefinedOrEmpty] = useState(false);

  const checkOngoingBenefits = () => {
    if (
      isUserReceivedOngoingBenefit === SubmissionRadioValue.Yes &&
      !ongoingBenefitAmount &&
      !ongoingBenefitFurtherPersonsNumber
    ) {
      setIsOngoingBenefitsValuesUndefinedOrEmpty(true);
    } else {
      setIsOngoingBenefitsValuesUndefinedOrEmpty(false);
    }
  };

  useEffect(() => {
    checkOngoingBenefits();
  }, [ongoingBenefitAmount, ongoingBenefitFurtherPersonsNumber]);

  const handleFormSubmit = async (data: SubmissionCreationFormValues) => {
    const isOngoingBenefitsIncomplete =
      isUserReceivedOngoingBenefit === SubmissionRadioValue.Yes &&
      !ongoingBenefitAmount &&
      !ongoingBenefitFurtherPersonsNumber;

    if (isOngoingBenefitsIncomplete) {
      setIsOngoingBenefitsValuesUndefinedOrEmpty(true);

      console.error('Not all fields are required!');
    } else {
      try {
        const consentGdprTextHash = await fetchAndHashPdf(policyPdfFile);
        const consentTermsTextHash = await fetchAndHashPdf(termsOfUsePdfFile);

        const dto = await convertSubmissionFormValuesToDto(data);
        const res = await createSubmission({ ...dto, consentGdprTextHash, consentTermsTextHash });
        setIsChangesUnsaved(false);

        if (res instanceof AxiosError) {
          const errorData = res?.response?.data;
          const message = `submissionCreation.filling.serverError[${errorData?.message}]`;

          const localeMessage =
            errorData?.message === 'Virus detected'
              ? formatMessage({ id: message }, { infectedFiles: errorData?.infectedFiles?.join(', ') })
              : formatMessage({ id: isLocaleMessageExists(message, messages) ? message : 'error.common' });

          enqueueSnackbar(localeMessage, {
            variant: 'error',
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          });
        } else {
          localStorage.removeItem(LocalStorageKey.EligibilityCheck);
          localStorage.removeItem(LocalStorageKey.SubmissionCreationMode);
          goToNextStep();
          dispatch(getCurrentSubmission());
        }
      } catch (error) {
        console.error('Error occurred:', error);
      }
    }
  };

  const contextValue = useMemo(() => ({ getChildrenValue, childrenFields, appendChild, removeChild }), [childrenFields]);

  const calculateAllowanceData = convertSubmissionFormValuesToExpectedAllowance(watch());

  useEffect(() => {
    if (isDirty) {
      setIsChangesUnsaved(true);
    }
  }, [calculateAllowanceData, isDirty]);

  const { data } = useCalculateAllowance(calculateAllowanceData);

  useEffect(() => {
    eventBus.changeSavingSubmitionStatus.broadcast('show');
    const unsubscribe = eventBus.saveSubmissionInFillingProcess.subscribe(async () => {
      try {
        eventBus.changeSavingSubmitionStatus.broadcast('pending');
        const formValues = getValues();
        if (submissionInFillingProcess?.data?.id) {
          const dto = await convertSubmissionFormValuesToUpdateDto(formValues);
          await updateSubmissionInFillingProcess({ id: submissionInFillingProcess?.data?.id, data: dto });
          eventBus.changeSavingSubmitionStatus.broadcast('loaded');

          return;
        }

        const dto = await convertSubmissionFormValuesToDto(formValues);
        await createSubmissionInFillingProcess(dto);
        eventBus.changeSavingSubmitionStatus.broadcast('loaded');
      } catch (error) {
        eventBus.changeSavingSubmitionStatus.broadcast('error');
      }
    });

    return () => {
      unsubscribe();
      eventBus.changeSavingSubmitionStatus.broadcast('hide');
    };
  }, [submissionInFillingProcess?.data?.id]);

  useEffect(() => {
    if (!isScrollActivated)
      return;

    setScrollActivated(false);
  }, [isScrollActivated]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isChangesUnsaved && !isEqual(prevFormValues, getValues())) {
        e.preventDefault();
        e.returnValue = '';
        return true;
      }
      return null;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isChangesUnsaved, prevFormValues]);

  const shouldBlockNavigation = () => {
    if (isChangesUnsaved && !isEqual(prevFormValues, getValues()) && !isScrollActivated) {
      setNavigateConfirmModal(true);
      return 'Are you sure you want to leave? You may lose unsaved changes.';
    }
    return null;
  };

  // @ts-ignore
  const blocker = useBlocker(shouldBlockNavigation);

  return (
    <SubmissionFormContext.Provider value={contextValue}>
      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(handleFormSubmit)}>
          <MaintenanceObligations />
          {!!childrenFields?.length && <ChildBenefit />}
          <OngoingServices isOngoingBenefitsValuesUndefinedOrEmpty={isOngoingBenefitsValuesUndefinedOrEmpty} />

          <CounselingRecognizedBody />

          <InfoBox />
          <StyledSubmissionFormFooter
            data={data}
            isSubmitting={isSubmitting}
            checkOngoingBenefits={checkOngoingBenefits}
            setScrollActivated={setScrollActivated}
            isOngoingBenefitsValuesUndefinedOrEmpty={isOngoingBenefitsValuesUndefinedOrEmpty}
          />
        </Form>
      </FormProvider>

      {navigateConfirmModal && (
        <ConfirmModal
          open={navigateConfirmModal}
          titleId="submissionCreation.confirmModal.loseUnsavedChanges.title"
          // @ts-ignore
          updateClick={() => blocker?.proceed()}
          handleClose={() => setNavigateConfirmModal(false)}
        />
      )}
    </SubmissionFormContext.Provider>
  );
};

export default SubmissionForm;
